import { Typography } from '@mui/material';
import { useEstate_PurgeAllEstatesMutation, useEstate_PurgeEstateMutation } from 'queries';

import { Button } from 'common/button';
import Modal from 'common/modal';
import ModalContent from 'common/modal-content';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

interface Props {
  estateId: string;
  portalId: string;
  setShowModal: (value: boolean) => void;
  showModal: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onPurgeReportDataComplete: Function;
  purgeAllExpiredEstateResults: boolean;
  setPurgeAllExpiredEstateResults: Function;
}

export default function PurgeModal({
  estateId,
  portalId,
  setShowModal,
  showModal,
  onPurgeReportDataComplete,
  purgeAllExpiredEstateResults,
  setPurgeAllExpiredEstateResults,
}: Props) {
  const onExitClick = () => {
    setPurgeAllExpiredEstateResults(false);
    setShowModal(false);
  };

  const [purgeEstate, { loading: purgeEstateExecuting }] = useEstate_PurgeEstateMutation({
    onCompleted: () => {
      showToast({
        message: 'The estate data has been purged.',
        severity: NotificationSeverity.SUCCESS,
      });
      onPurgeReportDataComplete();
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error purging estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [purgeAllEstates, { loading: purgeAllEstatesExecuting }] =
    useEstate_PurgeAllEstatesMutation({
      onCompleted: (data) => {
        showToast({
          message: `${data.data.length} estates have been purged.`,
          severity: NotificationSeverity.SUCCESS,
        });
        if (data.data.length > 0) {
          onPurgeReportDataComplete();
        }
        onExitClick();
      },
      onError: (e) => {
        showAlert({
          message: 'Error purging all estates: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const onPurgeClick = () => {
    if (purgeAllExpiredEstateResults) {
      purgeAllEstates();
    } else {
      purgeEstate({
        variables: {
          input: {
            portalId: portalId,
            id: estateId,
          },
        },
      });
    }
  };

  return (
    <Modal
      setShowModal={setShowModal}
      onExitClick={onExitClick}
      showModal={showModal}
      showActions={false}
      content={
        <ModalContent>
          <Typography variant="h5">Purge Report Data</Typography>
          <Typography>
            This action will remove cached credit and property reports, and will redact all data
            retrieved from Equifax. A new client consent will be required to reload the data.
          </Typography>
          <Typography>Are you sure you want to purge?</Typography>
          <Button onClick={onExitClick}>Cancel</Button>
          <Button
            onClick={onPurgeClick}
            color="secondary"
            disabled={purgeAllEstatesExecuting || purgeEstateExecuting}
          >
            Yes, purge data
          </Button>
        </ModalContent>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="md"
    />
  );
}
